import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";

/**
 * Buying a package, in one place.
 *
 * The pricing rows and a package's own page both sell the same thing, and the
 * rules around it are not obvious: Ultimate goes through the questionnaire,
 * guests sign in first, dynamic packages pick their libraries before paying.
 * Keeping that here means the two entry points cannot drift apart.
 */
export const usePackageCheckout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dynamicPackage, setDynamicPackage] = useState(null);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const initiatePayment = useCallback(async (pkg, selectedIds = []) => {
    setLoading(true);
    setError("");

    try {
      const { data } = await apiClient.post("/payments/initiate", {
        package_id: pkg.id,
        selected_library_ids: selectedIds,
      });

      const collectUrl = data.data?.collect_url;

      if (!collectUrl) {
        throw new Error("The payment provider did not return a payment link.");
      }

      window.location.href = collectUrl;
    } catch (exception) {
      setError(
        exception.response?.data?.message
          || Object.values(exception.response?.data?.errors || {})?.[0]?.[0]
          || exception.message
          || "Could not prepare payment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const startCheckout = useCallback((pkg) => {
    // Ultimate is requested, not bought outright: the questionnaire comes first.
    if (pkg.is_ultimate_program) {
      navigate("/my-program");
      return;
    }

    if (!user) {
      navigate("/auth");
      return;
    }

    if (pkg.is_dynamic) {
      setSelectedLibraryIds([]);
      setError("");
      setDynamicPackage(pkg);
      return;
    }

    initiatePayment(pkg);
  }, [user, navigate, initiatePayment]);

  const toggleLibrary = useCallback((libraryId) => {
    setSelectedLibraryIds((current) => (
      current.includes(libraryId)
        ? current.filter((id) => id !== libraryId)
        : [...current, libraryId]
    ));
  }, []);

  const closeDynamicPicker = useCallback(() => {
    if (!loading) setDynamicPackage(null);
  }, [loading]);

  return {
    startCheckout,
    loading,
    error,
    // Everything DynamicLibraryModal needs, ready to spread.
    dynamicPickerProps: {
      pkg: dynamicPackage,
      selectedIds: selectedLibraryIds,
      onToggle: toggleLibrary,
      onClose: closeDynamicPicker,
      onConfirm: () => initiatePayment(dynamicPackage, selectedLibraryIds),
      loading,
      error,
    },
  };
};
